import { liveQuery, type Subscription } from 'dexie';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { db, META_KEYS, readMeta, requestPersistentStorage } from '../modules/offline/db.js';
import { synchronisieren, type SyncOutcome, type SyncPhase } from '../modules/sync/engine.js';
import { useAuthStore } from './auth.js';

/**
 * Sichtbarer Synchronisationszustand.
 *
 * Die Oberfläche muss jederzeit ehrlich zeigen, ob Daten nur lokal liegen.
 * Deshalb gibt es genau einen Zustand, der aus Netzstatus, Warteschlange
 * und letztem Ergebnis abgeleitet wird.
 */

export type SyncZustand =
  | 'offline'
  | 'wartet'
  | 'synchronisiert'
  | 'laeuft'
  | 'fehler'
  | 'anmeldung-noetig'
  | 'konflikt';

/** Ruhiger Takt, wenn nichts aussteht. */
const RUHE_INTERVALL_MS = 60_000;
/** Erster Wiederholungsabstand, wenn noch Daten in der Warteschlange liegen. */
const WIEDERHOLUNG_START_MS = 5_000;
const WIEDERHOLUNG_MAX_MS = 30_000;

export const useSyncStore = defineStore('sync', () => {
  const online = ref(navigator.onLine);
  const phase = ref<SyncPhase>('idle');
  const offeneAenderungen = ref(0);
  const offeneKonflikte = ref(0);
  const letzteSynchronisation = ref<string | null>(null);
  const letzterFehler = ref<string | null>(null);
  const dauerhafterSpeicher = ref(false);

  let subscriptions: Subscription[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let wiederholungMs = WIEDERHOLUNG_START_MS;

  const zustand = computed<SyncZustand>(() => {
    const auth = useAuthStore();
    if (phase.value !== 'idle') return 'laeuft';
    if (auth.status === 'sitzung-abgelaufen') return 'anmeldung-noetig';
    if (!online.value) return 'offline';
    if (letzterFehler.value) return 'fehler';
    if (offeneKonflikte.value > 0) return 'konflikt';
    if (offeneAenderungen.value > 0) return 'wartet';
    return 'synchronisiert';
  });

  const zustandText = computed(() => {
    switch (zustand.value) {
      case 'laeuft':
        return phase.value === 'sende' ? 'Wird übertragen …' : 'Wird abgeglichen …';
      case 'offline':
        return offeneAenderungen.value > 0
          ? `Offline · ${offeneAenderungen.value} lokal gespeichert`
          : 'Offline · lokal verfügbar';
      case 'wartet':
        return `${offeneAenderungen.value} wartet auf Synchronisation`;
      case 'anmeldung-noetig':
        return 'Anmeldung erforderlich – Daten bleiben gespeichert';
      case 'konflikt':
        return `${offeneKonflikte.value} Konflikt${offeneKonflikte.value === 1 ? '' : 'e'} prüfen`;
      case 'fehler':
        return 'Synchronisation fehlgeschlagen';
      default:
        return 'Alles synchronisiert';
    }
  });

  /** Farbliche Einordnung für Kennzeichen und Statuspunkt. */
  const zustandTon = computed<'erfolg' | 'warnung' | 'fehler' | 'neutral'>(() => {
    switch (zustand.value) {
      case 'synchronisiert':
        return 'erfolg';
      case 'fehler':
      case 'anmeldung-noetig':
      case 'konflikt':
        return 'fehler';
      case 'wartet':
      case 'offline':
        return 'warnung';
      default:
        return 'neutral';
    }
  });

  async function starten(): Promise<void> {
    dauerhafterSpeicher.value = await requestPersistentStorage();
    letzteSynchronisation.value = await readMeta<string | null>(META_KEYS.lastSyncAt, null);

    subscriptions.push(
      liveQuery(() => db.outbox.count()).subscribe((count) => {
        // Eine neue lokale Änderung soll zügig rausgehen, nicht erst beim
        // nächsten Takt. Kein Netz? Dann greift die Wiederholung mit Abstand.
        const neueAenderung = count > offeneAenderungen.value;
        offeneAenderungen.value = count;
        if (neueAenderung && phase.value === 'idle') baldVersuchen();
      }),
      liveQuery(() => db.conflicts.filter((conflict) => conflict.resolvedAt === null).count()).subscribe(
        (count) => {
          offeneKonflikte.value = count;
        },
      ),
    );

    window.addEventListener('online', beiOnline);
    window.addEventListener('offline', beiOffline);
    document.addEventListener('visibilitychange', beiSichtbarkeit);

    void jetztSynchronisieren();
  }

  function beenden(): void {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
    subscriptions = [];
    window.removeEventListener('online', beiOnline);
    window.removeEventListener('offline', beiOffline);
    document.removeEventListener('visibilitychange', beiSichtbarkeit);
    if (timer) clearTimeout(timer);
  }

  function beiOnline(): void {
    online.value = true;
    // Verbindung zurück → sofort automatisch abgleichen.
    void jetztSynchronisieren();
  }

  function beiOffline(): void {
    online.value = false;
  }

  function beiSichtbarkeit(): void {
    if (document.visibilityState === 'visible') void jetztSynchronisieren();
  }

  /**
   * Plant den nächsten Versuch.
   *
   * Solange Daten in der Warteschlange liegen, wird mit wachsendem Abstand
   * erneut versucht (5 s, 10 s, 20 s … maximal 60 s). Damit kommt eine
   * Aufnahme auch dann zeitnah an, wenn das Betriebssystem gar kein
   * `online`-Ereignis meldet – auf Baustellen mit wackeligem Empfang der
   * Normalfall. Ist nichts offen, genügt der ruhige Minutentakt.
   */
  /** Kurz warten und dann übertragen – bündelt schnell aufeinanderfolgende Eingaben. */
  function baldVersuchen(ms = 1_500): void {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void jetztSynchronisieren(), ms);
  }

  function naechstenVersuchPlanen(letzterErfolg: boolean): void {
    if (timer) clearTimeout(timer);

    if (letzterErfolg) wiederholungMs = WIEDERHOLUNG_START_MS;

    const wartend = offeneAenderungen.value > 0;
    const abstand = wartend && !letzterErfolg ? wiederholungMs : RUHE_INTERVALL_MS;

    if (wartend && !letzterErfolg) {
      wiederholungMs = Math.min(wiederholungMs * 2, WIEDERHOLUNG_MAX_MS);
    }

    timer = setTimeout(() => void jetztSynchronisieren(), abstand);
  }

  async function jetztSynchronisieren(): Promise<SyncOutcome> {
    const auth = useAuthStore();
    const ergebnis = await synchronisieren((neuePhase) => {
      phase.value = neuePhase;
    });

    if (ergebnis.status === 'ok') {
      // Erfolgreicher Serverkontakt ist der verlässlichste Online-Nachweis.
      // `navigator.onLine` meldet z. B. in einem WLAN ohne Internetzugang
      // fälschlich "online".
      online.value = true;
      letzterFehler.value = null;
      letzteSynchronisation.value = await readMeta<string | null>(META_KEYS.lastSyncAt, null);
      if (auth.status !== 'angemeldet') await auth.sitzungPruefen();
    } else if (ergebnis.status === 'anmeldung-noetig') {
      online.value = true;
      auth.sitzungAbgelaufenMelden();
      letzterFehler.value = null;
    } else if (ergebnis.status === 'fehler') {
      letzterFehler.value = ergebnis.message ?? 'Unbekannter Fehler.';
    } else {
      online.value = false;
      letzterFehler.value = null;
    }

    naechstenVersuchPlanen(ergebnis.status === 'ok');
    return ergebnis;
  }

  return {
    online,
    phase,
    offeneAenderungen,
    offeneKonflikte,
    letzteSynchronisation,
    letzterFehler,
    dauerhafterSpeicher,
    zustand,
    zustandText,
    zustandTon,
    starten,
    beenden,
    jetztSynchronisieren,
  };
});
